import '~/utils/math-polyfills'

import { JsonRpcEngine, createAsyncMiddleware } from "json-rpc-engine";
import { providerFromEngine } from "eth-json-rpc-middleware";
import { CoinbaseWalletSDK } from "@coinbase/wallet-sdk";
import { createWalletClient, createPublicClient, http, custom, decodeAbiParameters, toFunctionHash } from "viem";
import { base, baseSepolia } from "viem/chains";
import { eip5792Actions } from "viem/experimental";
import Web3 from "web3";

import { configForChainId } from "./networkConfigs";
import { getBalance, ETH_TOKEN } from '~/services/ethereum'
import { USDC_ADDRESS } from "~/utils/constants"
import { isSameAddress } from "~/utils/validation";
import { sleep as waitFor } from "~/utils/helpers"; 

import { raribleBurnFunction, raribleMintAndTransferFunction, raribleSafeTransferFunction } from "~/abi/raribleFunctions";
import { erc1155Factory, erc721Factory, raribleCollection, raribleExchange, usdc } from "~/abi";

export const references = {
  initialized: false,
  provider: undefined,
  account: undefined,
  coinbaseSDK: undefined,
  web3: undefined,
  web3UserOp: undefined,
  walletClient: undefined,
  publicClient: undefined,
  networkConfig: undefined,
};

export const getProvider = () => references.provider;
export const getAccount = () => references.account;
export const getCoinbaseSDK = () => references.coinbaseSDK;
export const getWeb3 = () => references.web3;
export const getWeb3UserOp = () => references.web3UserOp;
export const getWalletClient = () => references.walletClient;
export const getPublicClient = () => references.publicClient;
export const getNetworkConfig = () => references.networkConfig;

export const connectAccount = async () => {
  useBaseMainnet();

  const coinbaseSDK = new CoinbaseWalletSDK({
    appName: "EBO",
    appChainIds: [getNetworkConfig().network.id],
    appLogoUrl: "https://pbs.twimg.com/profile_images/1763573006201229312/rWeK8REB_400x400.jpg",
  });

  const provider = coinbaseSDK.makeWeb3Provider({ options: "smartWalletOnly" });
  const [account] = await provider.request({ method: "eth_requestAccounts" });
  const web3 = new Web3(provider);
  const web3UserOp = new Web3(withUserOpMiddleware(provider));

  const walletClient = createWalletClient({
    chain: references.networkConfig.network,
    transport: custom({
      async request(args) {
        console.log("walletClient::request", args);
        const { method, params } = args;
        const response = await provider.request({ method, params });
        console.log("walletClient::response", response);
        return response;
      },
    }),
  }).extend(eip5792Actions());

  const publicClient = createPublicClient({
    chain: references.networkConfig.network,
    transport: http(),
  });

  references.coinbaseSDK = coinbaseSDK;
  references.provider = provider;
  references.account = account;
  references.web3 = web3;
  references.web3UserOp = web3UserOp;
  references.walletClient = walletClient;
  references.publicClient = publicClient;

  references.initialized = true;

  return account;
};

export const disconnectAccount = () => {
  getProvider().disconnect();

  references.coinbaseSDK = undefined;
  references.provider = undefined;
  references.account = undefined;
  references.web3 = undefined;
  references.walletClient = undefined;

  references.initialized = false;
};

export const signMessage = (message) => references.walletClient.signMessage({ message, account: getAccount() });

export const getUSDCBalance = () => getBalance(USDC_ADDRESS.replace(/(ETHEREUM:|BASE:)/g, ''), getAccount(), 6)

export const getETHBalance = () => getBalance(ETH_TOKEN.address, getAccount(), 18)

export const withUserOpMiddleware = (provider) => {
  const engine = new JsonRpcEngine();

  const userOpMiddleware = createAsyncMiddleware(async (req, res) => {
    console.log(req.method)
    if (req.method === "eth_sendTransaction") {
      const { to: address, data } = req.params[0];
      const functionName = data.substring(0, 10);
      const dataArgs = data.substring(11);
      const networkConfig = getNetworkConfig();
      
      let abi;
      if (isSameAddress(address, networkConfig.raribleSDK.exchange)) abi = raribleExchange;
      if (isSameAddress(address, networkConfig.currencies.usdc)) abi = usdc;
      if (isSameAddress(address, networkConfig.raribleSDK.erc721Factory)) abi = erc721Factory;
      if (isSameAddress(address, networkConfig.raribleSDK.erc1155Factory)) abi = erc1155Factory;
      if ([
          raribleMintAndTransferFunction,
          raribleBurnFunction,
          raribleSafeTransferFunction,
        ].includes(functionName)
      ) {
        abi = raribleCollection;
      }

      const abiItemsByHash = abi.reduce((result, abiItem) => {
        const functionHash = toFunctionHash(abiItem).substring(0, 10);
        result[functionHash] = abiItem;
        return result;
      }, {});

      const abiItem = abiItemsByHash[functionName];
      const args = decodeAbiParameters(abiItem.inputs, `0x${dataArgs}`);

      console.log("executing eth_sendTransaction", {
        functionName,
        dataArgs,
        args,
      });

      const account = getAccount();
      const walletClient = getWalletClient();
      const writeContractsConfig = {
        account,
        capabilities: {
          paymasterService: {
            url: getNetworkConfig().walletCapabilities.paymasterUrl,
          },
        },
        contracts: [{ address, abi, functionName, args }],
      };

      // const id = await writeContracts(walletClient, writeContractsConfig);
      const id = await walletClient.writeContracts(writeContractsConfig);
      let getCallResponse = await walletClient.getCallsStatus({ id });
      let { status } = getCallResponse;
      console.log("walletClient.getCallsStatus()", getCallResponse);

      let i = 0;
      while (status === "PENDING" && i < 10) {
        await waitFor(2000);
        getCallResponse = await walletClient.getCallsStatus({ id });
        console.log("walletClient.getCallsStatus()", getCallResponse);
        status = getCallResponse.status;
        i++;
      }
      res.result = getCallResponse.receipts[0].transactionHash;

    } else {
      res.result = await provider.request(req);
    }

    console.log("userOpMiddleware::result", req.method, req, res);
  });

  engine.push(userOpMiddleware);

  const result = providerFromEngine(engine);
  return result;
};

export const useBaseMainnet = () => (references.networkConfig = configForChainId(base.id));

export const useBaseTestnet = () => (references.networkConfig = configForChainId(baseSepolia.id));
